import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Box, Button, Dialog, DialogActions, DialogContent, Skeleton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import moment from "moment";
import React, { useContext, useMemo } from "react";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { EmailBody } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: 'fit-content',
            height: 'fit-content',
            padding: 16,
            borderRadius: 24,
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'unset',
        color: '#7bd4d4',
        fontWeight: 'bold',
        fontSize: '1.1rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        height: '100%',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    title: {
        display: '-webkit-box',
        width: 'fit-content',
        fontSize: '1.1rem',
        fontFamily: 'Poppins',
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        maxWidth: '24ch',
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['600'],
    },
    subject: {
        display: '-webkit-box',
        width: 'fit-content',
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
        overflow: 'hidden',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        maxWidth: '40ch',
        wordBreak: 'break-all',
    },
    ownership: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['500'],
    },
    owner: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.primary.light,
    },
    timestamp: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        textAlign: 'right',
    },
    icon: {
        width: 32,
        height: 32,
    },
    summaryContainer: {
        display: 'block',
        width: '100%',
        height: 'auto',
        background: 'linear-gradient(90deg, rgba(212,236,237,1) 0%, rgba(240,254,254,1) 100%)',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        borderRadius: 16,
        padding: 16,
        breakInside: 'avoid',
        transition: '0.05s ease-in-out',
    },
    simpleContainer: {
        background: '#fff',
        border: `1px solid ${theme.colors.neutral['300']}`,
        "&:hover": 'unset',
    },
    summaryContentBlock: {
        width: '100%',
        height: 'fit-content',
        maxHeight: '224px',
        overflowY: 'auto',
    },
    summaryTitle: {
        color: '#7bd4d4',
        fontSize: '1rem',
        fontWeight: 'bold',
        fontStyle: 'italic',
        fontFamily: 'Inter',
    },
    simpleTitle: {
        fontFamily: 'Inter',
        fontSize: '0.925rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['700'],
    },
    summaryText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: '#048290',
    },
    simpleText: {
        color: theme.colors.neutral['600'],
    },
    bodyText: {
        color: theme.colors.neutral['600'],
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    closeButton: {
        borderRadius: 16,
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        width: 80,
        height: 32
    },
    wave: {
        borderRadius: 4,
        height: 24,
        background: `linear-gradient(90deg, ${theme.colors.primary['200']} 0%, ${theme.colors.primary['100']} 50%, ${theme.colors.primary['50']} 100%)`,
        "&::after": {
            background: `linear-gradient(90deg, transparent, ${theme.colors.cyan['100']}, transparent)`,
        },
    },
}));

const EmailSummaryModal: React.FC<{
    emailContent?: EmailBody & { summary: string; },
    open: boolean,
    onClose: () => void,
}> = ({ emailContent, open, onClose }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);

    const queriesLoaded = useMemo(() => !!mappedOverviewQueries.size, [mappedOverviewQueries.size]);

    if (!emailContent)
        return (<></>);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={onClose}>
            <DialogContent className={classes.dialogContent}>
                <Stack direction="row" alignItems="flex-start" width="100%" key={'company-content-card-192-' + emailContent?.id}>
                    <Box width="40px" mt="4px">
                        <EmailIcon className={classes.icon} sx={{ fill: '#7bd4d4 !important'}} />
                    </Box>
                    <Stack direction="column" spacing={1} alignItems="flex-start" justifyContent="center" width="100%" py={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                            <Typography className={classes.subject} width="100%">
                                {emailContent?.subject?.replace('Fwd:', '')?.replace('Fw:', '') || '<Untitled>'}
                            </Typography>
                            <Typography className={classes.timestamp} width="30%">
                                {`${moment(emailContent?.createdAt).fromNow()}`}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                            <Typography className={classes.ownership}>
                                {'From: '}
                                <Typography component="span" className={classes.owner} display="inline" pl="4px">
                                    {!!emailContent.from ? emailContent.from.replace(/<.*?>/g, '').trim() : 'N/A'}
                                </Typography>
                            </Typography>
                        </Stack>
                        <Box />
                        <Stack className={classNames(classes.summaryContainer, classes.simpleContainer)} pt={2}>
                            <Typography className={classes.simpleTitle}>
                                {`Summary of this email`}
                            </Typography>
                            <Stack className={classes.summaryContentBlock}>
                                {queriesLoaded ? (
                                    <Typography className={classNames(classes.summaryText, classes.simpleText)}>
                                        {emailContent.summary || 'No summary yet.'}
                                    </Typography>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {[...Array(6)].map((_, i) => (
                                            <Skeleton animation="wave" className={classes.wave} key={'email-summary-modal-182-' + i} />
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                        <Stack spacing={1} pt={2} width="100%">
                            <Typography className={classes.simpleTitle} color="#666666 !important" pl={1}>
                                {`Original email:`}
                            </Typography>
                            <Stack className={classNames(classes.summaryContainer, classes.simpleContainer)} pt={2}>
                                <Stack className={classes.summaryContentBlock}>
                                    <Typography className={classes.bodyText}>
                                        {emailContent.content || 'No content.'}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="contained"
                    className={classes.closeButton}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                }}> Close </Button>
            </DialogActions>
        </Dialog>
    </>);
};

export default EmailSummaryModal;
